import { Trans, useLingui } from '@lingui/react/macro'
import { visitHooks } from '$src/lib/trpcHooks'
import { propertyHooks } from '$src/lib/trpcHooks/propertyHooks'
import { UserEmailBadge } from '$src/components/company-portal/UserEmailBadge'
import { PastVisit } from '$src/modules/visit/PastVisit'
import { FutureVisit } from '$src/modules/visit/FutureVisit'
import { Center, Loader, Text } from '@mantine/core'
import { AppPage } from '@markku/components'
import { isScheduledVisit } from '@markku/types'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

const searchSchema = z.object({
  feedback: z.literal('open').optional(),
  r: z.coerce.number().int().min(1).max(5).optional(),
})

function CompanyVisitDetail() {
  const { t } = useLingui()
  const { companyId, visitId } = Route.useParams()
  const search = Route.useSearch()

  const { data: visit, isLoading: isLoadingVisit } = visitHooks.useGetById(
    Number(visitId),
  )
  const { data: property, isLoading: isLoadingProperty } =
    propertyHooks.useGetById(visit?.propertyId ?? 0, {
      enabled: !!visit?.propertyId,
    })

  const isLoading = isLoadingVisit || isLoadingProperty
  const backTarget = `/company-portal/${companyId}/visits`

  if (isLoading) {
    return (
      <AppPage
        title={t`Visit`}
        backTarget={backTarget}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (!visit || (property && property.customerId !== Number(companyId))) {
    return (
      <AppPage
        title={t`Visit not found`}
        backTarget={backTarget}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Text c="dimmed">
            <Trans>Visit not found</Trans>
          </Text>
        </Center>
      </AppPage>
    )
  }

  // A past visit always has times — narrow to ScheduledVisit for the subtree.
  const pastVisit =
    isScheduledVisit(visit) && new Date(visit.startTime) < new Date()
      ? visit
      : null

  return (
    <AppPage
      title={pastVisit ? t`Past visit` : t`Upcoming visit`}
      backTarget={backTarget}
      rightSection={<UserEmailBadge />}
    >
      {pastVisit ? (
        <PastVisit
          visit={pastVisit}
          property={property}
          autoOpenFeedback={search.feedback === 'open'}
          initialFeedbackRating={search.r}
        />
      ) : (
        <FutureVisit visit={visit} />
      )}
    </AppPage>
  )
}

export const Route = createFileRoute(
  '/_app/company-portal/$companyId/visits/$visitId',
)({
  validateSearch: searchSchema,
  component: CompanyVisitDetail,
})
